import type {
	ModuleProps,
	T2Schema,
	TranslationKey,
} from "@typedefs/modulesInterfaces";
import {
	Settlements,
	type BuildingResponse,
	type ModuleFull,
	type OtherResponse,
	type RoadResponse,
} from "./settlementsTypes";
import GenericOptionalSubModule from "../GenericOptionalSubmodule";
import GenericModule from "../GenericModule";
import { memo, useEffect, useMemo } from "react";
import { useAppSelector, usePermissions } from "@app/hooks";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useCreateBuildingsMutation,
	useCreateOthersMutation,
	useCreateRoadsMutation,
	useDeleteBuildingsMutation,
	useDeleteOthersMutation,
	useDeleteRoadsMutation,
	useContextAwareGetBuildings,
	useContextAwareGetOthers,
	useContextAwareGetRoads,
} from "@hooks/index";
import { FEModules } from "@utils/moduleList";
import { useContextAwareGetLandUseChange } from "@hooks/index";
import { mergeLocalAndServerChanges } from "../inputs/utils";
import { useFormikContext } from "formik";
import { useSettlementContext } from "@contexts/SettlementProvider";
import { isEqual } from 'lodash';

interface SettlementTierOneProps {
	initValues: ModuleFull;
	t2Schema: T2Schema;
	setInitValues: React.Dispatch<React.SetStateAction<ModuleFull>>;
}

// Configuration object to reduce code duplication
const SETTLEMENT_CONFIG = {
	[Settlements.Roads]: {
		fieldName: "roads" as const,
		translationKey: "module.add_new_road" as const,
		payloadType: "RoadCreatePayload" as const,
	},
	[Settlements.Buildings]: {
		fieldName: "buildings" as const,
		translationKey: "module.add_new_building" as const,
		payloadType: "RoadCreatePayload" as const, // Note: seems like this should be BuildingCreatePayload based on the pattern
	},
	[Settlements.Others]: {
		fieldName: "others" as const,
		translationKey: "module.add_new_other" as const,
		payloadType: "OtherCreatePayload" as const,
	},
} as const;

const SettlementTierOne = ({
	moduleSchema,
	initValues,
	title,
	t2Schema,
	isLoading,
	beModuleType,
	blockingError,
	footerError,
	isError,
	setInitValues,
}: ModuleProps & SettlementTierOneProps) => {
	const moduleProps = {
		title,
		moduleSchema,
		footerError,
		blockingError,
		isError,
		isLoading,
	};
	const moduleContext = useSettlementContext();
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { isReadOnly } = usePermissions()
	
	// Mutation hooks
	const [deleteRoad, { isLoading: isLoadingDeleteRoad }] = useDeleteRoadsMutation();
	const [deleteBuildings, { isLoading: isLoadingDeleteBuilding }] = useDeleteBuildingsMutation();
	const [deleteOthers, { isLoading: isLoadingDeleteOther }] = useDeleteOthersMutation();
	const [addRoad, { isLoading: isLoadingAddRoad }] = useCreateRoadsMutation();
	const [addBuidling, { isLoading: isLoadingAddBuilding }] = useCreateBuildingsMutation();
	const [addOther, { isLoading: isLoadingAddOther }] = useCreateOthersMutation();
	
	// Mutation map for cleaner access
	const deleteMutations = {
		[Settlements.Roads]: deleteRoad,
		[Settlements.Buildings]: deleteBuildings,
		[Settlements.Others]: deleteOthers,
	};
	
	const addMutations = {
		[Settlements.Roads]: addRoad,
		[Settlements.Buildings]: addBuidling,
		[Settlements.Others]: addOther,
	};
	
	const addLoadingStates = {
		[Settlements.Roads]: isLoadingAddRoad,
		[Settlements.Buildings]: isLoadingAddBuilding,
		[Settlements.Others]: isLoadingAddOther,
	};

	const deleteLoadingStates = {
		[Settlements.Roads]: isLoadingDeleteRoad,
		[Settlements.Buildings]: isLoadingDeleteBuilding,
		[Settlements.Others]: isLoadingDeleteOther,
	};

	const lucId = moduleList.find((module) => module.id === FEModules.LandUseChange)?.uniqueId;
	const parentId = moduleList.find((m) => m.id === FEModules.Settlements)?.uniqueId;

	// Generic remove handler
	const createRemoveHandler = (settlementType: Settlements) => (
		remove: <T>(index: number) => T | undefined,
		index: number,
		id?: number | null,
	) => {
		remove(index);

		if (id) {
			const deleteMutation = deleteMutations[settlementType];
			deleteMutation({
				id,
				activityId: activeActivityId ?? 0,
				parent: parentId ?? 0,
			});
		}
	};

	// Generic add handler
	const createAddHandler = (settlementType: Settlements) => async () => {
		const payload = {
			body: { parent: parentId ?? 0 },
			activityId: activeActivityId ?? 0,
		};
		
		try {
			const addMutation = addMutations[settlementType];
			await addMutation(payload).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	// Simplified generic functions using configuration
	const genericDeleteHandler = (name: string) => {
		const settlementType = name as Settlements;
		return SETTLEMENT_CONFIG[settlementType] ? createRemoveHandler(settlementType) : () => {};
	};

	const genericAddHandler = (name: string) => {
		const settlementType = name as Settlements;
		return SETTLEMENT_CONFIG[settlementType] ? createAddHandler(settlementType) : () => {};
	};

	const genericValuesHandler = (name: string) => {
		const config = SETTLEMENT_CONFIG[name as Settlements];
		return config ? initValues[config.fieldName] : null;
	};

	const genericFieldArrayName = (name: string) => {
		const config = SETTLEMENT_CONFIG[name as Settlements];
		return config ? config.fieldName : "";
	};

	const translationKeySubmodule = (name: Settlements): TranslationKey => {
		const config = SETTLEMENT_CONFIG[name];
		return config ? config.translationKey : "module.add_new_road"; // fallback
	};

	/* ADD SUBMODULES LOGIC */
	const { values } = useFormikContext<ModuleFull>();
	const { data: roadsData, isLoading: isLoadingRoads } = useContextAwareGetRoads(
		{ activityId: activeActivityId ?? 0 },
		{
			refetchOnMountOrArgChange: true,
		},
	);
	const { data: buildingsData, isLoading: isLoadingBuildings } = useContextAwareGetBuildings(
		{ activityId: activeActivityId ?? 0 },
		{
			refetchOnMountOrArgChange: true,
		},
	);
	const { data: othersData, isLoading: isLoadingOthers } = useContextAwareGetOthers(
		{ activityId: activeActivityId ?? 0 },
		{
			refetchOnMountOrArgChange: true,
		},
	);
	const { data: lucData, isLoading: isLoadingLuc } = useContextAwareGetLandUseChange(lucId ?? 0, { refetchOnMountOrArgChange: true, skip: !lucId });

	// Helper functions for loading states
	const getDataLoadingStates = {
		[Settlements.Roads]: isLoadingRoads,
		[Settlements.Buildings]: isLoadingBuildings,
		[Settlements.Others]: isLoadingOthers,
	};

	const getOptionalLoadingState = (subModuleName: string) => {
		const settlementType = subModuleName as Settlements;
		return isLoadingLuc || (SETTLEMENT_CONFIG[settlementType] ? getDataLoadingStates[settlementType] : false);
	};

	const getCreateLoadingState = (subModuleName: string) => {
		const settlementType = subModuleName as Settlements;
		return SETTLEMENT_CONFIG[settlementType] ? addLoadingStates[settlementType] : false;
	};

	const getDeleteLoadingState = (subModuleName: string) => {
		const settlementType = subModuleName as Settlements;
		return SETTLEMENT_CONFIG[settlementType] ? deleteLoadingStates[settlementType] : false;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const lists = useMemo(() => {
		const isRoadsUpdated = !isEqual(values.roads, roadsData);
		const isBuildingsUpdated = !isEqual(values.buildings, buildingsData)
		const isOthersUpdated = !isEqual(values.others, othersData);
		const isLucUpdated = !isEqual(lucData, values.roads.map((road) => road.land_use_change)) || !isEqual(lucData, values.buildings.map((building) => building.land_use_change)) || !isEqual(lucData, values.others.map((other) => other.land_use_change));
		const roads: RoadResponse[] | undefined = roadsData?.map((road) => ({
			...road,
			land_use_change: lucData?.module,
		})) as RoadResponse[];
		const buildings: BuildingResponse[] | undefined = buildingsData?.map((building) => ({
			...building,
			land_use_change: lucData?.module,
		})) as BuildingResponse[];
		const others: OtherResponse[] | undefined = othersData?.map((other) => ({
			...other,
			land_use_change: lucData?.module,
		})) as OtherResponse[];
		return {
			roads: isRoadsUpdated || isLucUpdated
				? mergeLocalAndServerChanges({
					currentChangesList: values.roads.map((road) => ({ ...road, land_use_change: lucData?.module })) as RoadResponse[],
					incomingList: roads ?? [],
				})
				: values.roads,
			buildings: isBuildingsUpdated || isLucUpdated
				? mergeLocalAndServerChanges({
					currentChangesList: values.buildings.map((building) => ({ ...building, land_use_change: lucData?.module })) as BuildingResponse[],
					incomingList: buildings ?? [],
				})
				: values.buildings,
			others: isOthersUpdated || isLucUpdated
				? mergeLocalAndServerChanges({
					currentChangesList: values.others.map((other) => ({ ...other, land_use_change: lucData?.module })) as OtherResponse[],
					incomingList: others ?? [],
				})
				: values.others,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [roadsData, buildingsData, othersData, lucData]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (lists.roads || lists.buildings || lists.others) {
			setInitValues(() => ({
				...values,
				roads: lists.roads,
				buildings: lists.buildings,
				others: lists.others,
			}));
			if (moduleContext) {
				if (lists.buildings) moduleContext.setBuildingsIds(lists.buildings.map((building) => building.id));
				if (lists.roads) moduleContext.setRoadsIds(lists.roads.map((road) => road.id));
				if (lists.others) moduleContext.setOthersIds(lists.others.map((other) => other.id));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lists, setInitValues]);

	return (
		<GenericModule
			{...moduleProps}
			t2Schema={t2Schema}
			beModuleType={beModuleType}
			disableOptionals
		>
			{
				// not scalable implementation
				({ belongsToLuc, isSWW }) => (
					<>
						{moduleSchema.optionalSubmodules?.map((subModule, index) => {
							return (
								<GenericOptionalSubModule<
									RoadResponse | BuildingResponse | OtherResponse
								>
									key={`${subModule.name}-${index}`}
									optionalSchema={subModule}
									values={genericValuesHandler(subModule.name)}
									createHandler={genericAddHandler(subModule.name)}
									removeHandler={genericDeleteHandler(subModule.name)}
									fieldArrayName={genericFieldArrayName(subModule.name)}
									addLabel={translationKeySubmodule(subModule.name as Settlements)}
									belongsToLuc={belongsToLuc}
									isSWW={isSWW}
									isReadOnly={isReadOnly}
									isOptionalLoading={getOptionalLoadingState(subModule.name)}
									isCreateLoading={getCreateLoadingState(subModule.name)}
									isDeleteLoading={getDeleteLoadingState(subModule.name)}
								/>
							);
						})}
					</>
				)
			}
		</GenericModule>
	);
};

export default memo(SettlementTierOne);
